import { Dismiss12Regular } from "@fluentui/react-icons";
import { useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { todayISO } from "@/engine/date";
import { topDelays } from "@/engine/health";
import { scheduleHeadline } from "@/features/reports/report-model";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/date-format";
import { fmtDelta, fmtSpi } from "@/lib/format";
import { useSettingsHub } from "@/shell/settings-hub";
import { useProjectStore } from "@/store/project-store";
import { summarizeRecords } from "@/store/record-ops";
import { useSaveHealth } from "@/store/save-health";
import { ConnectionDot } from "@/desktop/ConnectionBadge";
import { isDesktop } from "@/desktop/runtime";
import { connectionText, useConnectionStatus } from "@/desktop/use-connection-status";

/** 누르면 다른 화면으로 가는 칸. 상태 바의 다른 글자와 같은 크기로 두고 호버·포커스만 얹는다. */
function Stat({ label, title, onClick, children }: { label: string; title: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-sm px-1 text-muted-foreground hover:bg-foreground/8 hover:text-foreground active:bg-foreground/12 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
    >
      {label}
      <span className="tabular">{children}</span>
    </button>
  );
}

/**
 * 일정 화면 하단의 진행 상황 한 줄. 현황 화면과 같은 계산(`scheduleHeadline`)을 쓰고,
 * 각 칸을 누르면 그 수치를 자세히 보는 화면(현황·이슈)으로 간다.
 */
export function StatusBar() {
  const navigate = useNavigate();
  const readOnly = usePlanSurface().readOnly;
  const activeId = useProjectStore((s) => s.activeId);
  const project = useProjectStore((s) => s.project);
  const derived = useProjectStore((s) => s.derived);
  const saveState = useProjectStore((s) => s.saveState);
  const lastError = useProjectStore((s) => s.lastError);
  const clearError = useProjectStore((s) => s.clearError);
  const selCount = useProjectStore((s) => s.selection.length);
  const saveFailed = useSaveHealth((s) => !!s.failure);
  const desktop = isDesktop();
  const connection = useConnectionStatus();

  const { schedule, evm, baseline, calendar } = derived;
  // derived.delays는 상위 5개로 잘려 있다. 현황과 같은 한도(50)로 다시 뽑아 지연 수가 일치하게 한다.
  const delays = useMemo(() => topDelays(project, schedule, baseline, 50, calendar), [project, schedule, baseline, calendar]);
  const head = useMemo(() => scheduleHeadline({ schedule, evm, delays }), [schedule, evm, delays]);
  const openIssues = useMemo(() => summarizeRecords(project.records ?? [], todayISO()).openIssues, [project.records]);

  const go = (tab: "reports" | "records") => activeId && navigate(`/projects/${activeId}/${tab}`);
  const late = head.spi !== null && head.spi < 0.9;

  return (
    <div className="flex h-6 shrink-0 items-center gap-1 overflow-hidden border-t border-border bg-background px-2 text-caption text-muted-foreground" aria-label="진행 상황">
      <Stat label="진척" title="현황에서 진척 자세히 보기" onClick={() => go("reports")}>
        <span className="text-foreground">{head.progress}%</span>
      </Stat>
      <Stat label="SPI" title={late ? "계획보다 많이 늦음 · 현황 보기" : "계획 대비 진척(SPI) · 현황 보기"} onClick={() => go("reports")}>
        <span className={cn(late ? "text-critical" : "text-foreground")}>{fmtSpi(head.spi)}</span>
        {late && <span className="sr-only"> 계획보다 많이 늦음</span>}
      </Stat>
      <Stat label="종료" title="예상 종료일 · 현황 보기" onClick={() => go("reports")}>
        <span className="text-foreground">{formatDate(head.projectFinish, "numeric")}</span>
        {head.hasBaseline && head.finishVarianceDays !== 0 && (
          <span className={cn("ml-1", head.finishVarianceDays > 0 ? "text-critical" : "text-muted-foreground")}>({fmtDelta(head.finishVarianceDays)})</span>
        )}
      </Stat>
      <Stat label="지연" title={head.hasBaseline ? "기준선보다 늦은 작업 · 현황 보기" : "기준선을 저장하면 지연을 계산해요"} onClick={() => go("reports")}>
        <span className={cn(head.delayCount > 0 ? "text-critical" : "text-foreground")}>{head.hasBaseline ? head.delayCount : "—"}</span>
      </Stat>
      <Stat label="열린 이슈" title="이슈 화면 열기" onClick={() => go("records")}>
        <span className="text-foreground">{openIssues}</span>
      </Stat>

      {lastError && (
        <span className="ml-2 flex min-w-0 items-center gap-1 text-critical">
          <span className="truncate">{lastError}</span>
          <button type="button" onClick={clearError} aria-label="닫기" className="rounded-sm hover:bg-foreground/8">
            <Dismiss12Regular className="size-3" />
          </button>
        </span>
      )}

      <span className="ml-auto flex shrink-0 items-center gap-3 whitespace-nowrap pl-3">
        <span>
          WBS <span className="text-foreground tabular">{project.tasks.length}</span>
          {selCount > 0 && <span className="ml-1">· 선택 {selCount}</span>}
        </span>
        {!readOnly && (saveFailed ? <span className="text-critical">저장 실패</span> : <span>{saveState === "saved" ? "저장됨" : "저장 중…"}</span>)}
        {desktop && (
          <button
            type="button"
            onClick={() => useSettingsHub.getState().open("app", "logs")}
            aria-label={`MCP ${connectionText(connection)} · 로그 열기`}
            title={`MCP: ${connectionText(connection)}`}
            className="inline-flex size-5 items-center justify-center rounded-sm hover:bg-foreground/8 active:bg-foreground/12 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
          >
            <ConnectionDot status={connection} />
          </button>
        )}
      </span>
    </div>
  );
}
